import type { DbColumn, DbConfig } from './config.ts';
import { dbActions, onDbData, runQuery, valueOf } from './runtime.ts';
import type { LocalNote } from '../data/types.ts';

/**
 * Die Tabelle eines Datenbank-Blocks. Sie lebt im Editor als Widget, also außerhalb
 * von React — deshalb schlichtes DOM statt Komponenten.
 *
 * Zellen schreiben direkt in das Frontmatter der jeweiligen Notiz; ein Klick auf den
 * Titel öffnet sie ganz normal. Bewusst nicht dabei: Relationen, Rollups und Formeln.
 * Genau die treiben Notions Komplexität hoch.
 */
export class DbTable {
  readonly dom = document.createElement('div');
  private viewSort: { key: string; direction: 'asc' | 'desc' } | null = null;
  private pending = false;
  private readonly unsubscribe: () => void;

  constructor(
    private readonly config: DbConfig,
    private readonly onEditQuery: () => void,
  ) {
    this.dom.className = 'db';
    this.unsubscribe = onDbData(() => this.refresh());
    this.dom.addEventListener('focusout', () => {
      // Nach dem Verlassen einer Zelle nachziehen, was während der Eingabe anlag.
      window.setTimeout(() => {
        if (this.pending && !this.dom.contains(document.activeElement)) this.refresh();
      }, 0);
    });
    this.render();
  }

  destroy(): void {
    this.unsubscribe();
  }

  /**
   * Neu zeichnen, aber nie mitten in einer Texteingabe — das würde den Cursor
   * verlieren. Auswahlfelder, Ankreuzfelder und Knöpfe sind mit einem Klick fertig
   * und dürfen sofort neu gezeichnet werden.
   */
  private refresh(): void {
    const active = document.activeElement;
    const typing =
      active instanceof HTMLInputElement &&
      ['text', 'number', 'url', 'date'].includes(active.type) &&
      this.dom.contains(active);
    if (typing) {
      this.pending = true;
      return;
    }
    this.pending = false;
    this.render();
  }

  private effectiveConfig(): DbConfig {
    return this.viewSort ? { ...this.config, sort: this.viewSort } : this.config;
  }

  private render(): void {
    const groups = runQuery(this.effectiveConfig());
    const total = groups.reduce((sum, group) => sum + group.rows.length, 0);
    this.dom.replaceChildren();

    const head = document.createElement('div');
    head.className = 'db-head';
    const label = document.createElement('span');
    label.className = 'db-label';
    label.textContent = describe(this.config);
    const count = document.createElement('span');
    count.className = 'db-count';
    count.textContent = total === 1 ? '1 Eintrag' : `${total} Einträge`;
    const edit = document.createElement('button');
    edit.type = 'button';
    edit.className = 'db-edit';
    edit.textContent = 'Abfrage bearbeiten';
    edit.addEventListener('click', () => this.onEditQuery());
    head.append(label, count, edit);
    this.dom.append(head);

    if (this.config.errors.length > 0) {
      const errors = document.createElement('p');
      errors.className = 'db-errors';
      errors.textContent = this.config.errors.join(' · ');
      this.dom.append(errors);
    }

    const scroll = document.createElement('div');
    scroll.className = 'db-scroll';
    const table = document.createElement('table');

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    for (const column of this.config.columns) {
      const cell = document.createElement('th');
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = column.label;
      const active = this.effectiveConfig().sort;
      if (active?.key === column.key) {
        button.textContent += active.direction === 'asc' ? ' ↑' : ' ↓';
      }
      button.addEventListener('click', () => {
        const current = this.effectiveConfig().sort;
        this.viewSort =
          current?.key === column.key && current.direction === 'asc'
            ? { key: column.key, direction: 'desc' }
            : { key: column.key, direction: 'asc' };
        this.render();
      });
      cell.append(button);
      headRow.append(cell);
    }
    thead.append(headRow);
    table.append(thead);

    const body = document.createElement('tbody');
    for (const group of groups) {
      if (this.config.group) {
        const groupRow = document.createElement('tr');
        groupRow.className = 'db-group';
        const groupCell = document.createElement('td');
        groupCell.colSpan = this.config.columns.length;
        groupCell.textContent = `${group.label} · ${group.rows.length}`;
        groupRow.append(groupCell);
        body.append(groupRow);
      }
      for (const note of group.rows) body.append(this.row(note));
      body.append(this.newRow(group.label));
    }
    if (groups.every((group) => group.rows.length === 0) && !this.config.group) {
      const emptyRow = document.createElement('tr');
      const emptyCell = document.createElement('td');
      emptyCell.colSpan = this.config.columns.length;
      emptyCell.className = 'db-empty';
      emptyCell.textContent = 'Keine Notiz passt auf diese Abfrage.';
      emptyRow.append(emptyCell);
      body.prepend(emptyRow);
    }
    table.append(body);
    scroll.append(table);
    this.dom.append(scroll);
  }

  private row(note: LocalNote): HTMLTableRowElement {
    const row = document.createElement('tr');
    for (const column of this.config.columns) {
      row.append(this.cell(note, column));
    }
    return row;
  }

  private cell(note: LocalNote, column: DbColumn): HTMLTableCellElement {
    const cell = document.createElement('td');
    const value = valueOf(note, column.key);

    if (column.key === 'title' || column.key === 'titel') {
      const link = document.createElement('button');
      link.type = 'button';
      link.className = 'db-title';
      link.textContent = value || 'Ohne Titel';
      link.addEventListener('click', () => dbActions()?.openNote(note.id));
      cell.append(link);
      return cell;
    }

    const write = (next: string): void => {
      void dbActions()?.setProperty(note.id, column.key, next);
    };

    switch (column.type) {
      case 'checkbox': {
        const box = document.createElement('input');
        box.type = 'checkbox';
        box.checked = /^(true|ja|x|1)$/i.test(value);
        box.addEventListener('change', () => write(box.checked ? 'true' : ''));
        cell.append(box);
        break;
      }
      case 'select': {
        const select = document.createElement('select');
        const options = column.options.includes(value) || value === '' ? column.options : [...column.options, value];
        select.append(new Option('—', ''));
        for (const option of options) select.append(new Option(option, option));
        select.value = value;
        select.addEventListener('change', () => write(select.value));
        cell.append(select);
        break;
      }
      case 'date': {
        const input = document.createElement('input');
        input.type = 'date';
        input.value = /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : '';
        input.addEventListener('change', () => write(input.value));
        cell.append(input);
        break;
      }
      case 'number': {
        const input = document.createElement('input');
        input.type = 'number';
        input.value = value;
        input.addEventListener('change', () => write(input.value));
        cell.append(input);
        break;
      }
      case 'tags': {
        cell.className = 'db-tags';
        for (const tag of value.split(',').map((entry) => entry.trim()).filter(Boolean)) {
          const chip = document.createElement('span');
          chip.className = 'db-chip';
          chip.textContent = tag;
          cell.append(chip);
        }
        break;
      }
      case 'url': {
        const input = document.createElement('input');
        input.type = 'url';
        input.value = value;
        input.addEventListener('change', () => write(input.value));
        cell.append(input);
        break;
      }
      default: {
        const input = document.createElement('input');
        input.type = 'text';
        input.value = value;
        input.addEventListener('change', () => write(input.value));
        cell.append(input);
      }
    }
    return cell;
  }

  private newRow(group: string): HTMLTableRowElement {
    const row = document.createElement('tr');
    row.className = 'db-add';
    const cell = document.createElement('td');
    cell.colSpan = this.config.columns.length;
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = '+ Neue Zeile';
    button.addEventListener('click', () => {
      void dbActions()?.createRow(this.config, this.config.group ? group : null);
    });
    cell.append(button);
    row.append(cell);
    return row;
  }
}

function describe(config: DbConfig): string {
  const parts = config.from.map((clause) => {
    if (clause.kind === 'folder') return `Ordner „${clause.name}"`;
    if (clause.kind === 'tag') return `#${clause.name}`;
    return 'Alle Notizen';
  });
  return parts.join(' und ');
}
